import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Divider,
  IconButton,
} from '@mui/material';
import {
  ArrowBack,
  CheckCircle,
  Download,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import authService from '../services/auth';

const Invoice = () => {
  const navigate = useNavigate();
  const [currentPlan, setCurrentPlan] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isNovalinkEmployee, setIsNovalinkEmployee] = useState(false);

  useEffect(() => {
    loadInvoiceData();
    checkNovalinkEmployee();
  }, []);

  const checkNovalinkEmployee = async () => {
    try {
      const response = await authService.getProfile();
      const userData = response?.data?.user || response?.user || response;
      const email = userData?.email || '';
      setIsNovalinkEmployee(email.toLowerCase().endsWith('@novalink-solutions.com'));
    } catch (error) {
      console.error('Error checking user email:', error);
    }
  };

  const loadInvoiceData = async () => {
    try {
      setLoading(true);
      
      // Check if user is Novalink employee first
      const response = await authService.getProfile();
      const userData = response?.data?.user || response?.user || response;
      const email = userData?.email || '';
      const isNovalink = email.toLowerCase().endsWith('@novalink-solutions.com');
      
      if (isNovalink) {
        // Novalink employees have free Pro plan
        const mockPlan = {
          name: 'Pro',
          price: 0,
          billingPeriod: 'Monthly',
          startDate: '2024-01-15',
          nextBillingDate: null,
          status: 'Active',
          canceledDate: null,
        };
        setCurrentPlan(mockPlan);
        setLoading(false);
        return;
      }
      
      // TODO: Replace with actual API calls
      // Mock data for now - replace with real API endpoints
      const subscriptionStatus = localStorage.getItem('subscriptionStatus');
      const canceledDate = localStorage.getItem('subscriptionCanceledDate');
      const userPlan = localStorage.getItem('userPlan') || 'Basic';
      
      const mockPlan = {
        name: userPlan === 'None' ? 'None' : userPlan,
        price: userPlan === 'Pro' ? 50 : 
               userPlan === 'Plus' ? 15 : 
               userPlan === 'None' ? 0 : 10,
        billingPeriod: 'Monthly',
        startDate: '2024-01-15',
        nextBillingDate: subscriptionStatus === 'canceled' ? null : '2024-02-15',
        status: subscriptionStatus === 'canceled' ? 'Canceled' : 'Active',
        canceledDate: canceledDate || null,
      };
      
      const mockPayments = [
        {
          id: '1',
          date: '2024-01-15',
          amount: 10,
          plan: 'Basic',
          status: 'Paid',
          invoiceId: 'INV-2024-001',
        },
        {
          id: '2',
          date: '2023-12-15',
          amount: 10,
          plan: 'Basic',
          status: 'Paid',
          invoiceId: 'INV-2023-012',
        },
        {
          id: '3',
          date: '2023-11-15',
          amount: 10,
          plan: 'Basic',
          status: 'Paid',
          invoiceId: 'INV-2023-011',
        },
      ];
      
      setCurrentPlan(mockPlan);
      setPaymentHistory(mockPayments);
    } catch (error) {
      console.error('Error loading invoice data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount) => {
    return `$${amount.toFixed(2)}`;
  };

  const getPlanColor = (planName) => {
    switch (planName) {
      case 'Pro':
        return '#1976d2';
      case 'Plus':
        return '#1565c0';
      default:
        return '#2c3e50';
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="lg">
        <IconButton
          onClick={() => navigate('/billing')}
          sx={{
            mb: 3,
            color: '#1976d2',
            '&:hover': {
              bgcolor: 'rgba(25, 118, 210, 0.1)',
            },
          }}
        >
          <ArrowBack />
        </IconButton>

        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            mb: 4,
            color: '#2c3e50',
          }}
        >
          Invoice & Billing
        </Typography>

        {/* Current Plan Card */}
        <Card
          sx={{
            mb: 4,
            borderRadius: 3,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    color: getPlanColor(currentPlan?.name),
                  }}
                >
                  Current Plan: {currentPlan?.name}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'rgba(44, 62, 80, 0.7)',
                    mb: 2,
                  }}
                >
                  {formatCurrency(currentPlan?.price)} / {currentPlan?.billingPeriod}
                </Typography>
              </Box>
              <Chip
                icon={currentPlan?.status === 'Canceled' ? undefined : <CheckCircle />}
                label={currentPlan?.status}
                color={currentPlan?.status === 'Canceled' ? 'error' : 'success'}
                sx={{
                  fontWeight: 600,
                }}
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(44, 62, 80, 0.6)',
                    mb: 0.5,
                  }}
                >
                  Start Date
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    color: '#2c3e50',
                  }}
                >
                  {formatDate(currentPlan?.startDate)}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(44, 62, 80, 0.6)',
                    mb: 0.5,
                  }}
                >
                  {currentPlan?.status === 'Canceled' ? 'Canceled Date' : 'Next Billing Date'}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    color: currentPlan?.status === 'Canceled' ? '#d32f2f' : '#2c3e50',
                  }}
                >
                  {currentPlan?.status === 'Canceled' && currentPlan?.canceledDate
                    ? formatDate(currentPlan.canceledDate)
                    : currentPlan?.nextBillingDate
                    ? formatDate(currentPlan.nextBillingDate)
                    : 'N/A'}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Payment History */}
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                mb: 3,
                color: '#2c3e50',
              }}
            >
              Payment History
            </Typography>

            <TableContainer component={Paper} sx={{ boxShadow: 'none', borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'rgba(25, 118, 210, 0.05)' }}>
                    <TableCell sx={{ fontWeight: 600, color: '#2c3e50' }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#2c3e50' }}>Plan</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#2c3e50' }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#2c3e50' }}>Invoice ID</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#2c3e50' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#2c3e50' }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paymentHistory.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No payment history found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paymentHistory.map((payment) => (
                      <TableRow key={payment.id} hover>
                        <TableCell>{formatDate(payment.date)}</TableCell>
                        <TableCell>
                          <Chip
                            label={payment.plan}
                            size="small"
                            sx={{
                              bgcolor: getPlanColor(payment.plan),
                              color: 'white',
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {formatCurrency(payment.amount)}
                        </TableCell>
                        <TableCell>{payment.invoiceId}</TableCell>
                        <TableCell>
                          <Chip
                            label={payment.status}
                            size="small"
                            color="success"
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            sx={{
                              color: '#1976d2',
                              '&:hover': {
                                bgcolor: 'rgba(25, 118, 210, 0.1)',
                              },
                            }}
                          >
                            <Download fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default Invoice;
